
import { Link as RouterLink, type LinkProps as RouterLinkProps } from "react-router-dom";
import { useState, type ImgHTMLAttributes, ReactNode } from "react";

import { buttonVariants } from "@/components/ui/button";
import { getSupportEmail } from "@/lib/support";
import { cn } from "@/lib/utils";

type ThemedShellProps = {
  children: ReactNode;
};

type AppLinkProps = Omit<RouterLinkProps, "to"> & {
  to?: RouterLinkProps["to"];
  href?: RouterLinkProps["to"];
};

function Link({ to, href, ...props }: AppLinkProps) {
  return <RouterLink to={to ?? href ?? "/"} {...props} />;
}

type ImageProps = ImgHTMLAttributes<HTMLImageElement> & { priority?: boolean };

function Image({ priority: _priority, ...props }: ImageProps) {
  return <img {...props} />;
}

const stats = [
  {
    value: "+5k",
    label: (
      <>
        Trusted by <br />
        <span className="whitespace-nowrap">best businesses</span>
      </>
    ),
  },
  {
    value: "80%",
    label: (
      <>
        Clients <br /> returning
      </>
    ),
  },
  {
    value: "99%",
    label: (
      <>
        Customer <br /> satisfaction
      </>
    ),
  },
  {
    value: "10+",
    label: (
      <>
        Years of <br />
        <span className="whitespace-nowrap">industry experience</span>
      </>
    ),
  },
];

export function ThemedShell({ children }: ThemedShellProps) {
  const support = getSupportEmail();
  const [isClicked, setIsClicked] = useState(false);

  const handleClick = () => {
    setIsClicked(true);
    setTimeout(() => setIsClicked(false), 1500);
  };
  return (
    <div data-landing-page className="flex min-h-screen flex-col bg-[#f5f5f5] font-sans">
      <main className="flex-1">{children}</main>

      <footer className="relative overflow-hidden border-t border-white/10 bg-[#111217] text-white">
        {/* ================= MARQUEE ================= */}
        <style>{`
          @keyframes marquee-stats {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee-stats {
            animation: marquee-stats 60s linear infinite;
          }
        `}</style>
        <div className="relative h-[50px] md:h-[75px] lg:h-[100px] overflow-hidden border-b border-white/10">
          {/* Fade edges */}
          <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-[60px] bg-[linear-gradient(90deg,#18171A,transparent)]" />
          <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-[60px] bg-[linear-gradient(270deg,#18171A,transparent)]" />

          {/* 2 identical copies - translateX(-50%) snaps back to start seamlessly */}
          <div className="flex w-max animate-marquee-stats">
            <MarqueeRow />
            <MarqueeRow />
          </div>
        </div>

        {/* ================= CTA ================= */}
        <div
          className="relative mx-auto flex w-full flex-col items-center px-[30px] pt-[80px] md:pt-[100px] lg:pt-[126px]"
          style={{
            backgroundImage: "url('/Images/footer/bg.png')",
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="relative z-10 flex max-w-[620px] flex-col items-center text-center gap-[50px]">
            <div className="rounded-full border border-[#242325] bg-[#18171A] px-[14px] py-[10px] text-[13px]">
              Let&apos;s go
            </div>

            <h2 className="text-[40px] md:text-[50px] lg:text-[60px] font-bold leading-none">
              <span className="block">Let&apos;s create</span>
              <span className="block">something together.</span>
            </h2>

            <p className="max-w-[494px] text-[#B2B2B2] text-[16px] md:text-[18px]">
              We are here to turn your ideas into designs that work-let&apos;s
              chat about your vision.
            </p>
            <div className="p-[5px] bg-[#18171A] border border-[#3C3B3D] rounded-full">
              <a
                href={`mailto:${support}`}
                className={cn(
                  buttonVariants({ variant: "outline", size: "lg" }),
                  "flex gap-[14px] items-center h-[48px] px-[34px]  !text-[#101114] no-underline dark:!bg-zinc-950/80 dark:!text-zinc-100 text-[13px] font-medium",
                )}
              >
                <Image
                  src="/Images/footer/book.svg"
                  alt=""
                  width={10}
                  height={12}
                  className="h-[12px] w-[10px]"
                />
                Book a call
              </a>
            <Image
  src="/Images/footer/rectangle-2.png"
  alt=""
  width={740}
  height={1}
  className="h-[1px] w-[740px] absolute left-[-1%] bottom-[7%] z-[-1]"
/>
            </div>
          </div>

          {/* ================= BIG TEXT ================= */}
<h1
  onClick={handleClick}
  className="mt-[120px] text-center uppercase cursor-pointer group relative w-full"
  aria-label="SWIFTMVP"
>
  {/* BASE GRADIENT TEXT (stable = no cutting) */}
  <span
    className="
      inline-block
      font-bold italic
      text-[45px] md:text-[120px] lg:text-[180px] xl:text-[240px]
      leading-[1.05]
      bg-[linear-gradient(180deg,rgba(255,255,255,0.35),rgba(24,23,26,0))]
      bg-clip-text text-transparent
      transition-all duration-700
      group-hover:opacity-0
      w-full
    "
  >
    SWIFTMVP
  </span>

  {/* WHITE FILL LAYER */}
  <span
    className="
      absolute inset-0
      font-bold italic
      text-[45px] md:text-[120px] lg:text-[180px] xl:text-[240px]
      leading-[1.05]
      text-white
      opacity-0
      transition-opacity duration-700
      group-hover:opacity-100
      w-full
    "
  >
    SWIFTMVP
  </span>
</h1>
        </div>

        {/* ================= FOOTER BAR ================= */}
        <div className="flex h-[80px] items-center border-t border-white/10 px-6">
          <div className="mx-auto flex w-full flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[14px] text-[#FFFFFFCC]">
              Swift MVP © 2026. All rights reserved.
            </p>
            <div className="flex items-center gap-[20px]">
              <Link
                href="/terms"
                className="text-[14px] text-[#FFFFFFCC] hover:opacity-80"
              >
                Terms
              </Link>
              <Link
                href="/privacy"
                className="text-[14px] text-[#FFFFFFCC] hover:opacity-80"
              >
                Privacy
              </Link>
              <Link
                href="/refunds"
                className="text-[14px] text-[#FFFFFFCC] hover:opacity-80"
              >
                Refunds
              </Link>
              {/* <span className="text-[#FFFFFFCC]">Made with 🤍</span> */}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ================= SUB COMPONENTS ================= */

function MarqueeRow() {
  return (
    <div className="flex">
      {Array.from({ length: 4 }).flatMap((_, setIdx) =>
        stats.map((item, index) => (
          <StatItem key={`${setIdx}-${index}`} {...item} />
        ))
      )}
    </div>
  );
}

function StatItem({
  value,
  label,
}: {
  value: string;
  label: React.ReactNode;
}) {
  return (
    <div className="flex items-center h-[50px] md:h-[75px] lg:h-[100px] whitespace-nowrap">
      <div className="flex items-center gap-[14px] px-[25px] md:px-[40px] lg:px-[60px]">
        <span className="text-[22px] md:text-[36px] lg:text-[50px] font-medium text-white">
          {value}
        </span>
        <span className="text-[8px] md:text-[10px] lg:text-[13px] leading-[120%] text-[#FFFFFFCC]">
          {label}
        </span>
      </div>
      <div className="h-[16px] md:h-[24px] lg:h-[32px] w-[1px] bg-[#FFFFFF1A]" />
    </div>
  );
}